/**
 * Curated icon and emoji data for the FetchIconEmojiPicker.
 * Provides a safe, searchable set of icons (from iconoir-react) and emojis
 * suitable for business/event contexts.
 */

import type { ReactNode } from "react";
import {
  Activity,
  Airplane,
  Apple,
  Archive,
  Basketball,
  Bathroom,
  BeachBag,
  Bed,
  Bicycle,
  BirthdayCake,
  Book,
  BookmarkBook,
  BookStack,
  BowlingBall,
  BreadSlice,
  Bus,
  ChatBubbleQuestion,
  CheckCircle,
  ClipboardCheck,
  AntennaSignal,
  Bank,
  Bell,
  Bookmark,
  BubbleStar,
  Building,
  Calculator,
  Calendar,
  Camera,
  Car,
  Cart,
  ChatBubble,
  CinemaOld,
  City,
  CloudSunny,
  Clock,
  Cloud,
  Code,
  CoffeeCup,
  Coins,
  Community,
  Compass,
  Computer,
  Cookie,
  CreditCard,
  Crown,
  Cutlery,
  Cycling,
  Delivery,
  DocStar,
  Dollar,
  EditPencil,
  Egg,
  FireFlame,
  Fish,
  Fishing,
  Folder,
  Flower,
  Football,
  Garage,
  Gas,
  Gift,
  GraphUp,
  Hammer,
  GlassEmpty,
  GlassHalf,
  Globe,
  Golf,
  Group,
  Gym,
  HalfMoon,
  Hashtag,
  Headset,
  Heart,
  HelpCircle,
  Home,
  Hospital,
  HotAirBalloon,
  Hourglass,
  HouseRooms,
  IceCream,
  Industry,
  InfoCircle,
  Journal,
  Key,
  Lamp,
  Laptop,
  Leaf,
  LightBulb,
  List,
  Lock,
  MagicWand,
  Mail,
  Map,
  MapPin,
  Medal,
  MediaImage,
  Megaphone,
  Metro,
  Microphone,
  MoonSat,
  Motorcycle,
  Movie,
  MusicDoubleNote,
  Notes,
  OrangeSlice,
  OrganicFood,
  Package,
  Packages,
  Page,
  PageEdit,
  Palette,
  Parking,
  PeopleTag,
  Phone,
  PiggyBank,
  Pin,
  PizzaSlice,
  Printer,
  ProfileCircle,
  Puzzle,
  QuestionMark,
  Rain,
  Reports,
  Ruler,
  Running,
  SeaAndSun,
  SeaWaves,
  SecurityPass,
  Server,
  ShoppingBag,
  Sparks,
  StatsReport,
  Settings,
  Shield,
  ShieldAlert,
  Shop,
  Skateboarding,
  SnowFlake,
  SoccerBall,
  Sofa,
  Spark,
  Star,
  Stretching,
  Suitcase,
  SunLight,
  Swimming,
  TennisBall,
  ThumbsUp,
  Timer,
  TimeZone,
  Tools,
  Tower,
  Train,
  Tree,
  Trekking,
  Trophy,
  Truck,
  Tv,
  User,
  UserCircle,
  UserPlus,
  VideoCamera,
  Walking,
  Wallet,
  WarningCircle,
  WarningHexagon,
  WarningTriangle,
  Weight,
  Wifi,
  Wrench,
  Yoga,
  // Conversation / messaging
  ChatBubbleEmpty,
  ChatLines,
  MessageText,
  MailOpen,
  QuoteMessage,
  Send,
  Translate,
  // Music / fun / dancing
  Album,
  EmojiSatisfied,
  EmojiSurprise,
  MicrophoneSpeaking,
  MusicDoubleNotePlus,
  // Creativity / art / design
  CollageFrame,
  ColorFilter,
  ColorPicker,
  ColorWheel,
  DesignNib,
  DesignPencil,
  FillColor,
  Frame,
  Lens,
  SparkSolid,
  // Food & drink
  GlassFragile,
  HalfCookie,
  OrangeSliceAlt,
  OrganicFoodSquare,
  Vegan,
  VeganCircle,
  // Hotel / concierge
  BedReady,
  // Misc — fun & accessories
  Bonfire,
  Glasses,
  Hat,
  HeartArrowDown,
  // More party / fun / games / drinks
  BrightCrown,
  CrownCircle,
  DiceFive,
  DiceOne,
  EmojiTalkingHappy,
  GlassHalfAlt,
  HexagonDice,
  MaskSquare,
  UserCrown,
  // Bags
  Bag,
  Handbag,
  // Kids / couples
  Stroller,
  Rings,
  // Sports & activities
  BoxingGlove,
  TennisBallAlt,
  BasketballField,
  FootballBall,
  WeightAlt,
} from "iconoir-react";
import {
  Wine as LucideWine,
  BottleWine as LucideBottleWine,
  Beer as LucideBeer,
  Martini as LucideMartini,
  GlassWater as LucideGlassWater,
  PartyPopper as LucidePartyPopper,
  CupSoda as LucideCupSoda,
  // Luggage / bags
  Luggage as LucideLuggage,
  Backpack as LucideBackpack,
  BaggageClaim as LucideBaggageClaim,
  BriefcaseBusiness as LucideBriefcaseBusiness,
  BriefcaseConveyorBelt as LucideBriefcaseConveyorBelt,
  // Food
  Cake as LucideCake,
  CakeSlice as LucideCakeSlice,
  Pizza as LucidePizza,
  Donut as LucideDonut,
  Sandwich as LucideSandwich,
  Salad as LucideSalad,
  Soup as LucideSoup,
  Hamburger as LucideHamburger,
  Popcorn as LucidePopcorn,
  Drumstick as LucideDrumstick,
  Beef as LucideBeef,
  Croissant as LucideCroissant,
  ChefHat as LucideChefHat,
  Utensils as LucideUtensils,
  UtensilsCrossed as LucideUtensilsCrossed,
  Candy as LucideCandy,
  // Travel & outdoor
  Plane as LucidePlane,
  PlaneTakeoff as LucidePlaneTakeoff,
  PlaneLanding as LucidePlaneLanding,
  Sailboat as LucideSailboat,
  Ship as LucideShip,
  Tent as LucideTent,
  TentTree as LucideTentTree,
  // Nature
  Mountain as LucideMountain,
  MountainSnow as LucideMountainSnow,
  TreePine as LucideTreePine,
  TreeDeciduous as LucideTreeDeciduous,
  Sunrise as LucideSunrise,
  Sunset as LucideSunset,
  MoonStar as LucideMoonStar,
  Flame as LucideFlame,
  // Events / music
  Sparkles as LucideSparkles,
  Drum as LucideDrum,
  Guitar as LucideGuitar,
  MicVocal as LucideMicVocal,
  Clapperboard as LucideClapperboard,
  // Misc
  Award as LucideAward,
  HeartHandshake as LucideHeartHandshake,
  HeartPulse as LucideHeartPulse,
  GraduationCap as LucideGraduationCap,
  Footprints as LucideFootprints,
  // Animals
  Dog as LucideDog,
  Cat as LucideCat,
  Bird as LucideBird,
  Rabbit as LucideRabbit,
  Turtle as LucideTurtle,
  Squirrel as LucideSquirrel,
  // Emotions
  Smile as LucideSmile,
  Laugh as LucideLaugh,
  // Hands / hospitality
  Handshake as LucideHandshake,
  HandHeart as LucideHandHeart,
  HandCoins as LucideHandCoins,
  HandPlatter as LucideHandPlatter,
  ConciergeBell as LucideConciergeBell,
  // Wellness / health
  Brain as LucideBrain,
  Stethoscope as LucideStethoscope,
  Eye as LucideEye,
  // Holidays / fun
  Ghost as LucideGhost,
  Origami as LucideOrigami,
  Diamond as LucideDiamond,
  // Creative tools
  Paintbrush as LucidePaintbrush,
  NotepadText as LucideNotepadText,
  Newspaper as LucideNewspaper,
  Receipt as LucideReceipt,
  Pencil as LucidePencil,
  // Construction
  HardHat as LucideHardHat,
  Construction as LucideConstruction,
  // Kids
  Baby as LucideBaby,
  ToyBrick as LucideToyBrick,
  Milk as LucideMilk,
  Sprout as LucideSprout,
  Sticker as LucideSticker,
  PersonStanding as LucidePersonStanding,
  // Family / group
  Users as LucideUsers,
  UsersRound as LucideUsersRound,
  // Couples / romance
  HeartPlus as LucideHeartPlus,
  Ribbon as LucideRibbon,
  VenetianMask as LucideVenetianMask,
  Theater as LucideTheater,
  // Tech / dev
  Bot as LucideBot,
  Database as LucideDatabase,
  Cpu as LucideCpu,
  Terminal as LucideTerminal,
  Smartphone as LucideSmartphone,
  Tablet as LucideTablet,
  FileText as LucideFileText,
  Inbox as LucideInbox,
  NotebookPen as LucideNotebookPen,
  // Commerce / money
  Store as LucideStore,
  Tag as LucideTag,
  Tags as LucideTags,
  Barcode as LucideBarcode,
  Banknote as LucideBanknote,
  Bitcoin as LucideBitcoin,
  Euro as LucideEuro,
  // Sports
  Volleyball as LucideVolleyball,
  // Apparel / misc
  Shirt as LucideShirt,
  Watch as LucideWatch,
  Wheat as LucideWheat,
  // More sports & activities
  Dumbbell as LucideDumbbell,
  Bike as LucideBike,
  Target as LucideTarget,
  Crosshair as LucideCrosshair,
  Goal as LucideGoal,
  Flag as LucideFlag,
  FlagTriangleRight as LucideFlagTriangleRight,
  Gamepad2 as LucideGamepad2,
  Joystick as LucideJoystick,
  Swords as LucideSwords,
} from "lucide-react";

// ============================================================
// Types
// ============================================================

/** Discriminator for whether a selection is an icon or emoji */
export type IconEmojiType = "icon" | "emoji";

/** Serializable value representing a picked icon or emoji */
export interface IconEmojiValue {
  type: IconEmojiType;
  /** Icon name (e.g. "Spark") or emoji character (e.g. "🏢") */
  value: string;
}

// ============================================================
// Icon Data
// ============================================================

interface IconEntry {
  name: string;
  keywords: string[];
  component: React.ComponentType<{ className?: string; strokeWidth?: string | number }>;
}

/** Curated icon set from iconoir-react */
export const CURATED_ICONS: IconEntry[] = [
  // Business
  { name: "Building", keywords: ["office", "corporate", "company", "building"], component: Building },
  { name: "Shop", keywords: ["store", "retail", "business", "shop"], component: Shop },
  { name: "Bank", keywords: ["finance", "money", "bank", "institution"], component: Bank },
  { name: "CreditCard", keywords: ["payment", "card", "credit", "purchase"], component: CreditCard },
  { name: "Dollar", keywords: ["money", "cost", "price", "budget", "dollar"], component: Dollar },
  { name: "Suitcase", keywords: ["work", "job", "briefcase", "professional"], component: Suitcase },
  {
    name: "LucideBriefcaseBusiness",
    keywords: ["briefcase", "business", "work", "professional", "office", "career"],
    component: LucideBriefcaseBusiness,
  },
  {
    name: "LucideBriefcaseConveyorBelt",
    keywords: ["briefcase", "conveyor", "luggage", "airport", "baggage", "claim", "travel"],
    component: LucideBriefcaseConveyorBelt,
  },
  { name: "Wallet", keywords: ["wallet", "money", "cash", "payments"], component: Wallet },
  { name: "Coins", keywords: ["coins", "money", "currency", "change"], component: Coins },
  { name: "PiggyBank", keywords: ["savings", "piggy bank", "budget", "money"], component: PiggyBank },
  { name: "Cart", keywords: ["cart", "shopping", "purchase", "buy"], component: Cart },
  { name: "ShoppingBag", keywords: ["shopping", "bag", "retail", "purchase"], component: ShoppingBag },
  { name: "Calculator", keywords: ["calculator", "math", "budget", "finance"], component: Calculator },
  { name: "GraphUp", keywords: ["growth", "chart", "trend", "increase", "analytics"], component: GraphUp },
  { name: "StatsReport", keywords: ["stats", "report", "analytics", "data", "summary"], component: StatsReport },
  { name: "Industry", keywords: ["factory", "industry", "manufacturing", "plant"], component: Industry },
  { name: "Tools", keywords: ["tools", "fix", "repair", "maintenance"], component: Tools },
  { name: "Hammer", keywords: ["hammer", "build", "construction", "tool"], component: Hammer },
  { name: "Wrench", keywords: ["wrench", "fix", "repair", "tool", "settings"], component: Wrench },
  { name: "Package", keywords: ["package", "box", "delivery", "shipping"], component: Package },
  { name: "Packages", keywords: ["boxes", "packages", "shipping", "inventory"], component: Packages },
  { name: "Delivery", keywords: ["delivery", "shipping", "order", "logistics"], component: Delivery },
  {
    name: "LucideStore",
    keywords: ["store", "shop", "retail", "business", "storefront"],
    component: LucideStore,
  },
  {
    name: "LucideTag",
    keywords: ["tag", "label", "price", "category", "sale"],
    component: LucideTag,
  },
  {
    name: "LucideTags",
    keywords: ["tags", "labels", "categories", "filter", "sort"],
    component: LucideTags,
  },
  {
    name: "LucideBarcode",
    keywords: ["barcode", "scan", "product", "inventory", "sku"],
    component: LucideBarcode,
  },
  {
    name: "LucideBanknote",
    keywords: ["banknote", "money", "cash", "bill", "currency"],
    component: LucideBanknote,
  },
  {
    name: "LucideBitcoin",
    keywords: ["bitcoin", "crypto", "btc", "cryptocurrency", "blockchain"],
    component: LucideBitcoin,
  },
  {
    name: "LucideEuro",
    keywords: ["euro", "eur", "money", "currency", "europe"],
    component: LucideEuro,
  },

  // People
  { name: "Group", keywords: ["team", "people", "group", "users", "attendees"], component: Group },
  { name: "User", keywords: ["person", "profile", "user", "individual"], component: User },
  { name: "UserCircle", keywords: ["person", "profile", "avatar", "account"], component: UserCircle },
  { name: "UserPlus", keywords: ["invite", "add user", "new", "signup", "register"], component: UserPlus },
  { name: "ProfileCircle", keywords: ["profile", "avatar", "user", "account"], component: ProfileCircle },
  { name: "PeopleTag", keywords: ["attendees", "people", "tag", "group", "list"], component: PeopleTag },
  { name: "Crown", keywords: ["leader", "vip", "premium", "crown", "executive"], component: Crown },
  { name: "Community", keywords: ["community", "network", "social", "together"], component: Community },
  { name: "ThumbsUp", keywords: ["like", "approve", "good", "thumbs", "positive"], component: ThumbsUp },
  { name: "Running", keywords: ["active", "fitness", "run", "exercise", "sport"], component: Running },

  // Kids & Family
  {
    name: "LucideBaby",
    keywords: ["baby", "infant", "newborn", "child", "kid", "family"],
    component: LucideBaby,
  },
  {
    name: "Stroller",
    keywords: ["stroller", "baby", "pram", "buggy", "kid", "family"],
    component: Stroller,
  },
  {
    name: "LucideMilk",
    keywords: ["milk", "bottle", "baby", "drink", "feeding"],
    component: LucideMilk,
  },
  {
    name: "LucideToyBrick",
    keywords: ["toy", "lego", "brick", "kid", "play", "build"],
    component: LucideToyBrick,
  },
  {
    name: "LucideSticker",
    keywords: ["sticker", "kid", "decoration", "fun", "craft"],
    component: LucideSticker,
  },
  {
    name: "LucideSprout",
    keywords: ["sprout", "grow", "kid", "young", "plant", "new"],
    component: LucideSprout,
  },
  {
    name: "LucidePersonStanding",
    keywords: ["person", "standing", "kid", "child", "figure"],
    component: LucidePersonStanding,
  },
  {
    name: "LucideUsers",
    keywords: ["family", "group", "team", "people", "users"],
    component: LucideUsers,
  },
  {
    name: "LucideUsersRound",
    keywords: ["family", "group", "team", "circle", "community", "people"],
    component: LucideUsersRound,
  },

  // Couples & Romance
  {
    name: "Rings",
    keywords: ["rings", "wedding", "engagement", "marriage", "couple", "jewelry"],
    component: Rings,
  },
  {
    name: "LucideHeartPlus",
    keywords: ["heart", "add to favorites", "match", "like", "love", "follow"],
    component: LucideHeartPlus,
  },
  {
    name: "LucideRibbon",
    keywords: ["ribbon", "bow", "gift", "wedding", "award", "decoration"],
    component: LucideRibbon,
  },
  {
    name: "LucideVenetianMask",
    keywords: ["masquerade", "mask", "ball", "romance", "carnival", "venice"],
    component: LucideVenetianMask,
  },
  {
    name: "LucideTheater",
    keywords: ["theater", "date night", "play", "performance", "show"],
    component: LucideTheater,
  },

  // Events
  { name: "Calendar", keywords: ["date", "schedule", "event", "calendar", "time"], component: Calendar },
  {
    name: "HotAirBalloon",
    keywords: ["adventure", "travel", "balloon", "fun", "experience"],
    component: HotAirBalloon,
  },
  { name: "MusicDoubleNote", keywords: ["music", "entertainment", "concert", "party"], component: MusicDoubleNote },
  { name: "VideoCamera", keywords: ["video", "recording", "film", "media", "camera"], component: VideoCamera },
  { name: "Headset", keywords: ["audio", "support", "headset", "conference", "call"], component: Headset },
  { name: "Gym", keywords: ["fitness", "workout", "gym", "health", "exercise"], component: Gym },
  { name: "Gift", keywords: ["gift", "present", "swag", "giveaway", "reward"], component: Gift },
  { name: "Medal", keywords: ["medal", "award", "winner", "recognition", "honor"], component: Medal },
  {
    name: "Microphone",
    keywords: ["mic", "speaker", "presenter", "keynote", "talk", "podcast"],
    component: Microphone,
  },
  {
    name: "MicrophoneSpeaking",
    keywords: ["speaking", "presenter", "talk", "keynote", "live", "podcast"],
    component: MicrophoneSpeaking,
  },
  {
    name: "MusicDoubleNotePlus",
    keywords: ["music", "playlist", "add song", "dj", "party"],
    component: MusicDoubleNotePlus,
  },
  { name: "Album", keywords: ["album", "music", "record", "vinyl", "playlist"], component: Album },
  { name: "EmojiSatisfied", keywords: ["happy", "smile", "fun", "joy", "emoji"], component: EmojiSatisfied },
  { name: "EmojiSurprise", keywords: ["surprise", "wow", "shocked", "excited", "emoji"], component: EmojiSurprise },
  { name: "Movie", keywords: ["movie", "film", "cinema", "screening", "entertainment"], component: Movie },
  {
    name: "MaskSquare",
    keywords: ["mask", "theater", "performance", "drama", "show", "costume", "party"],
    component: MaskSquare,
  },
  {
    name: "EmojiTalkingHappy",
    keywords: ["happy", "talk", "fun", "smile", "party", "social", "chat"],
    component: EmojiTalkingHappy,
  },
  { name: "DiceOne", keywords: ["dice", "game", "play", "luck", "casino", "party"], component: DiceOne },
  { name: "DiceFive", keywords: ["dice", "game", "play", "luck", "casino", "party"], component: DiceFive },
  {
    name: "HexagonDice",
    keywords: ["dice", "d6", "game", "play", "tabletop", "rpg", "party"],
    component: HexagonDice,
  },
  {
    name: "BrightCrown",
    keywords: ["crown", "vip", "party", "celebrate", "winner", "sparkle"],
    component: BrightCrown,
  },
  {
    name: "CrownCircle",
    keywords: ["crown", "vip", "premium", "leader", "executive", "circle"],
    component: CrownCircle,
  },
  { name: "UserCrown", keywords: ["vip", "host", "leader", "premium", "user", "guest"], component: UserCrown },
  {
    name: "LucidePartyPopper",
    keywords: ["party", "popper", "celebrate", "confetti", "festive", "new year", "birthday"],
    component: LucidePartyPopper,
  },
  {
    name: "LucideSparkles",
    keywords: ["sparkles", "magic", "shiny", "ai", "highlight", "stars"],
    component: LucideSparkles,
  },
  {
    name: "LucideDrum",
    keywords: ["drum", "music", "band", "percussion", "rhythm"],
    component: LucideDrum,
  },
  {
    name: "LucideGuitar",
    keywords: ["guitar", "music", "rock", "band", "instrument", "strings"],
    component: LucideGuitar,
  },
  {
    name: "LucideMicVocal",
    keywords: ["microphone", "vocal", "sing", "karaoke", "performer", "stage"],
    component: LucideMicVocal,
  },
  {
    name: "LucideClapperboard",
    keywords: ["clapperboard", "film", "movie", "production", "action", "scene"],
    component: LucideClapperboard,
  },

  // Activities & Sports
  { name: "Swimming", keywords: ["swim", "pool", "water", "sport", "activity"], component: Swimming },
  { name: "Yoga", keywords: ["yoga", "meditation", "wellness", "stretch", "mindfulness"], component: Yoga },
  { name: "Stretching", keywords: ["stretch", "warmup", "flexibility", "exercise"], component: Stretching },
  { name: "Weight", keywords: ["weight", "lifting", "strength", "gym", "dumbbell"], component: Weight },
  { name: "Golf", keywords: ["golf", "sport", "club", "course", "activity"], component: Golf },
  { name: "TennisBall", keywords: ["tennis", "sport", "racket", "ball", "activity"], component: TennisBall },
  { name: "SoccerBall", keywords: ["soccer", "football", "sport", "ball", "game"], component: SoccerBall },
  { name: "Football", keywords: ["football", "american", "sport", "game", "ball"], component: Football },
  { name: "Basketball", keywords: ["basketball", "sport", "ball", "hoop", "game"], component: Basketball },
  { name: "BowlingBall", keywords: ["bowling", "sport", "game", "activity", "fun"], component: BowlingBall },
  { name: "Cycling", keywords: ["cycling", "bike", "ride", "sport", "exercise"], component: Cycling },
  { name: "Fishing", keywords: ["fishing", "fish", "rod", "lake", "outdoor"], component: Fishing },
  {
    name: "Skateboarding",
    keywords: ["skateboard", "skate", "sport", "activity", "extreme"],
    component: Skateboarding,
  },
  { name: "Activity", keywords: ["activity", "pulse", "health", "heartbeat", "vitals"], component: Activity },
  {
    name: "LucideVolleyball",
    keywords: ["volleyball", "sport", "ball", "beach", "game"],
    component: LucideVolleyball,
  },
  {
    name: "BoxingGlove",
    keywords: ["boxing", "glove", "fight", "sport", "mma", "punch"],
    component: BoxingGlove,
  },
  {
    name: "TennisBallAlt",
    keywords: ["tennis", "ball", "sport", "racket", "alt"],
    component: TennisBallAlt,
  },
  {
    name: "BasketballField",
    keywords: ["basketball", "court", "field", "sport", "game", "arena"],
    component: BasketballField,
  },
  {
    name: "FootballBall",
    keywords: ["football", "ball", "soccer", "sport", "game"],
    component: FootballBall,
  },
  {
    name: "WeightAlt",
    keywords: ["weight", "dumbbell", "lift", "gym", "strength", "fitness"],
    component: WeightAlt,
  },
  {
    name: "LucideDumbbell",
    keywords: ["dumbbell", "gym", "weight", "lift", "fitness", "strength", "workout"],
    component: LucideDumbbell,
  },
  {
    name: "LucideBike",
    keywords: ["bike", "cycle", "bicycle", "ride", "sport"],
    component: LucideBike,
  },
  {
    name: "LucideTarget",
    keywords: ["target", "goal", "aim", "archery", "darts", "bullseye", "focus"],
    component: LucideTarget,
  },
  {
    name: "LucideCrosshair",
    keywords: ["crosshair", "aim", "precision", "target", "focus", "shooting"],
    component: LucideCrosshair,
  },
  {
    name: "LucideGoal",
    keywords: ["goal", "objective", "milestone", "achievement", "score"],
    component: LucideGoal,
  },
  {
    name: "LucideFlag",
    keywords: ["flag", "finish", "race", "milestone", "country", "marker"],
    component: LucideFlag,
  },
  {
    name: "LucideFlagTriangleRight",
    keywords: ["flag", "golf", "course", "marker", "pennant"],
    component: LucideFlagTriangleRight,
  },
  {
    name: "LucideGamepad2",
    keywords: ["gamepad", "controller", "video game", "gaming", "console", "play"],
    component: LucideGamepad2,
  },
  {
    name: "LucideJoystick",
    keywords: ["joystick", "arcade", "retro", "gaming", "controller"],
    component: LucideJoystick,
  },
  {
    name: "LucideSwords",
    keywords: ["swords", "battle", "duel", "competition", "fight", "tournament"],
    component: LucideSwords,
  },

  // Alerts
  {
    name: "WarningTriangle",
    keywords: ["warning", "caution", "alert", "danger", "important"],
    component: WarningTriangle,
  },
  {
    name: "WarningCircle",
    keywords: ["warning", "caution", "alert", "error", "important"],
    component: WarningCircle,
  },
  {
    name: "WarningHexagon",
    keywords: ["warning", "caution", "hazard", "danger", "stop"],
    component: WarningHexagon,
  },
  { name: "ShieldAlert", keywords: ["security", "alert", "warning", "caution", "protect"], component: ShieldAlert },
  { name: "InfoCircle", keywords: ["info", "information", "help", "notice", "about"], component: InfoCircle },
  { name: "HelpCircle", keywords: ["help", "question", "faq", "support", "ask"], component: HelpCircle },
  { name: "QuestionMark", keywords: ["question", "ask", "faq", "unknown", "help"], component: QuestionMark },
  {
    name: "ChatBubbleQuestion",
    keywords: ["question", "ask", "faq", "inquiry", "support", "chat"],
    component: ChatBubbleQuestion,
  },

  // Travel
  { name: "Airplane", keywords: ["flight", "travel", "airplane", "trip", "international"], component: Airplane },
  { name: "Map", keywords: ["directions", "navigation", "map", "location"], component: Map },
  { name: "MapPin", keywords: ["location", "place", "pin", "destination", "address"], component: MapPin },
  { name: "Globe", keywords: ["world", "international", "global", "earth"], component: Globe },
  { name: "Compass", keywords: ["navigate", "explore", "compass", "direction"], component: Compass },
  { name: "Car", keywords: ["drive", "transport", "car", "vehicle", "ride"], component: Car },
  { name: "Bus", keywords: ["bus", "transit", "public", "transport", "shuttle"], component: Bus },
  { name: "Train", keywords: ["train", "rail", "transit", "transport", "railway"], component: Train },
  { name: "Metro", keywords: ["metro", "subway", "underground", "transit", "transport"], component: Metro },
  { name: "Bicycle", keywords: ["bike", "cycle", "bicycle", "ride", "transport"], component: Bicycle },
  { name: "Motorcycle", keywords: ["motorcycle", "scooter", "motorbike", "ride"], component: Motorcycle },
  { name: "Truck", keywords: ["delivery", "shipping", "truck", "logistics"], component: Truck },
  { name: "Walking", keywords: ["walk", "pedestrian", "stroll", "on foot", "directions"], component: Walking },
  { name: "Parking", keywords: ["parking", "park", "garage", "car", "lot"], component: Parking },
  { name: "Gas", keywords: ["gas", "fuel", "petrol", "station", "refuel"], component: Gas },
  { name: "Garage", keywords: ["garage", "parking", "car", "storage", "repair"], component: Garage },
  {
    name: "SecurityPass",
    keywords: ["passport", "badge", "pass", "id", "visa", "travel", "identity"],
    component: SecurityPass,
  },
  { name: "Bed", keywords: ["bed", "hotel", "room", "sleep", "accommodation"], component: Bed },
  {
    name: "BedReady",
    keywords: ["bed", "made", "hotel", "housekeeping", "turndown", "concierge", "service"],
    component: BedReady,
  },
  {
    name: "Bathroom",
    keywords: ["bathroom", "shower", "amenity", "restroom", "hotel"],
    component: Bathroom,
  },
  { name: "BeachBag", keywords: ["beach", "vacation", "travel", "bag", "summer"], component: BeachBag },
  {
    name: "LucideLuggage",
    keywords: ["luggage", "suitcase", "travel", "trip", "vacation", "bag", "rolling"],
    component: LucideLuggage,
  },
  {
    name: "LucideBaggageClaim",
    keywords: ["baggage", "claim", "airport", "luggage", "travel", "arrival"],
    component: LucideBaggageClaim,
  },
  {
    name: "LucideBackpack",
    keywords: ["backpack", "bag", "travel", "school", "hike", "rucksack"],
    component: LucideBackpack,
  },
  { name: "Bag", keywords: ["bag", "tote", "carry", "purse", "shopping"], component: Bag },
  { name: "Handbag", keywords: ["handbag", "purse", "bag", "fashion", "accessory"], component: Handbag },
  { name: "Hospital", keywords: ["hospital", "medical", "clinic", "health", "emergency"], component: Hospital },
  { name: "City", keywords: ["city", "skyline", "urban", "downtown"], component: City },
  { name: "Tower", keywords: ["tower", "landmark", "skyline", "building"], component: Tower },
  { name: "HouseRooms", keywords: ["rooms", "house", "floor plan", "interior"], component: HouseRooms },
  { name: "SeaAndSun", keywords: ["beach", "vacation", "sea", "sun", "scenic"], component: SeaAndSun },
  { name: "MoonSat", keywords: ["moon", "night", "evening", "weather"], component: MoonSat },
  {
    name: "LucidePlane",
    keywords: ["plane", "airplane", "flight", "travel", "trip"],
    component: LucidePlane,
  },
  {
    name: "LucidePlaneTakeoff",
    keywords: ["takeoff", "departure", "plane", "flight", "leaving"],
    component: LucidePlaneTakeoff,
  },
  {
    name: "LucidePlaneLanding",
    keywords: ["landing", "arrival", "plane", "flight", "arriving"],
    component: LucidePlaneLanding,
  },
  {
    name: "LucideSailboat",
    keywords: ["sailboat", "boat", "yacht", "sea", "ocean"],
    component: LucideSailboat,
  },
  {
    name: "LucideShip",
    keywords: ["ship", "boat", "cruise", "ocean", "voyage"],
    component: LucideShip,
  },
  {
    name: "LucideTent",
    keywords: ["tent", "camping", "outdoor", "campsite", "shelter"],
    component: LucideTent,
  },
  {
    name: "LucideTentTree",
    keywords: ["camping", "tent", "tree", "outdoor", "nature", "campground"],
    component: LucideTentTree,
  },
  {
    name: "LucideFootprints",
    keywords: ["footprints", "walk", "trail", "path", "journey", "steps"],
    component: LucideFootprints,
  },

  // Communication
  { name: "ChatBubble", keywords: ["chat", "message", "conversation", "talk", "discuss"], component: ChatBubble },
  {
    name: "ChatBubbleEmpty",
    keywords: ["chat", "conversation", "message", "speak", "empty"],
    component: ChatBubbleEmpty,
  },
  {
    name: "ChatLines",
    keywords: ["conversation", "chat", "discussion", "thread", "comments"],
    component: ChatLines,
  },
  { name: "MessageText", keywords: ["message", "text", "sms", "note", "dm"], component: MessageText },
  {
    name: "QuoteMessage",
    keywords: ["quote", "testimonial", "citation", "speech", "blockquote"],
    component: QuoteMessage,
  },
  { name: "Send", keywords: ["send", "submit", "share", "post", "deliver"], component: Send },
  { name: "Mail", keywords: ["email", "inbox", "message", "letter", "send"], component: Mail },
  { name: "MailOpen", keywords: ["mail", "opened", "read", "email", "delivered"], component: MailOpen },
  { name: "Phone", keywords: ["call", "phone", "contact", "mobile"], component: Phone },
  { name: "Megaphone", keywords: ["announce", "marketing", "megaphone", "broadcast"], component: Megaphone },
  { name: "Bell", keywords: ["notification", "alert", "bell", "reminder", "concierge"], component: Bell },
  { name: "AntennaSignal", keywords: ["signal", "wireless", "connection", "network"], component: AntennaSignal },
  {
    name: "Translate",
    keywords: ["translate", "language", "international", "convert", "interpreter"],
    component: Translate,
  },

  // Tech
  { name: "Laptop", keywords: ["computer", "tech", "laptop", "device", "work"], component: Laptop },
  { name: "Computer", keywords: ["computer", "desktop", "monitor", "screen", "pc"], component: Computer },
  { name: "Tv", keywords: ["tv", "television", "screen", "monitor", "display"], component: Tv },
  { name: "Wifi", keywords: ["wifi", "internet", "wireless", "connection", "network"], component: Wifi },
  { name: "Printer", keywords: ["printer", "print", "office", "document", "copy"], component: Printer },
  { name: "Server", keywords: ["server", "hosting", "data", "rack", "backend"], component: Server },
  { name: "Code", keywords: ["development", "programming", "code", "tech"], component: Code },
  { name: "Cloud", keywords: ["cloud", "storage", "hosting", "data"], component: Cloud },
  { name: "Shield", keywords: ["security", "protection", "shield", "safe"], component: Shield },
  { name: "Settings", keywords: ["settings", "config", "gear", "options"], component: Settings },
  { name: "Puzzle", keywords: ["integration", "puzzle", "connect", "solution"], component: Puzzle },
  { name: "Lock", keywords: ["secure", "private", "lock", "protected"], component: Lock },
  { name: "Sofa", keywords: ["sofa", "couch", "lounge", "furniture", "room"], component: Sofa },
  { name: "Lamp", keywords: ["lamp", "light", "interior", "room", "furniture"], component: Lamp },
  {
    name: "LucideBot",
    keywords: ["bot", "robot", "ai", "automation", "chatbot"],
    component: LucideBot,
  },
  {
    name: "LucideDatabase",
    keywords: ["database", "db", "storage", "data", "records"],
    component: LucideDatabase,
  },
  {
    name: "LucideCpu",
    keywords: ["cpu", "chip", "processor", "tech", "hardware"],
    component: LucideCpu,
  },
  {
    name: "LucideTerminal",
    keywords: ["terminal", "console", "command line", "shell", "code"],
    component: LucideTerminal,
  },
  {
    name: "LucideSmartphone",
    keywords: ["smartphone", "phone", "mobile", "device", "iphone"],
    component: LucideSmartphone,
  },
  {
    name: "LucideTablet",
    keywords: ["tablet", "ipad", "device", "mobile", "screen"],
    component: LucideTablet,
  },
  {
    name: "LucideFileText",
    keywords: ["file", "text", "document", "page", "doc"],
    component: LucideFileText,
  },
  {
    name: "LucideInbox",
    keywords: ["inbox", "messages", "mail", "queue", "tray"],
    component: LucideInbox,
  },
  {
    name: "LucideNotebookPen",
    keywords: ["notebook", "journal", "write", "notes", "diary"],
    component: LucideNotebookPen,
  },

  // Creative
  { name: "Spark", keywords: ["idea", "spark", "magic", "creative", "innovation"], component: Spark },
  { name: "Sparks", keywords: ["sparkle", "magic", "creative", "shiny", "ai"], component: Sparks },
  { name: "SparkSolid", keywords: ["spark", "magic", "highlight", "feature", "ai"], component: SparkSolid },
  { name: "MagicWand", keywords: ["magic", "wand", "ai", "spell", "transform"], component: MagicWand },
  { name: "FireFlame", keywords: ["fire", "trending", "hot", "popular", "flame"], component: FireFlame },
  { name: "LightBulb", keywords: ["idea", "insight", "lightbulb", "solution", "bright"], component: LightBulb },
  { name: "Palette", keywords: ["design", "art", "palette", "color", "creative"], component: Palette },
  { name: "ColorPicker", keywords: ["color", "picker", "swatch", "art", "design"], component: ColorPicker },
  { name: "ColorWheel", keywords: ["color wheel", "spectrum", "palette", "art", "design"], component: ColorWheel },
  { name: "ColorFilter", keywords: ["filter", "color", "effect", "tint", "photo"], component: ColorFilter },
  { name: "FillColor", keywords: ["fill", "paint", "bucket", "color", "design"], component: FillColor },
  { name: "DesignNib", keywords: ["design", "nib", "pen", "ink", "calligraphy", "art"], component: DesignNib },
  { name: "DesignPencil", keywords: ["design", "pencil", "sketch", "draft", "art"], component: DesignPencil },
  { name: "Camera", keywords: ["photo", "picture", "camera", "capture"], component: Camera },
  { name: "Lens", keywords: ["lens", "camera", "photography", "zoom", "focus"], component: Lens },
  { name: "MediaImage", keywords: ["image", "picture", "photo", "gallery"], component: MediaImage },
  { name: "Frame", keywords: ["frame", "picture", "photo", "wall art", "border"], component: Frame },
  {
    name: "CollageFrame",
    keywords: ["collage", "photos", "gallery", "grid", "frames"],
    component: CollageFrame,
  },
  { name: "CinemaOld", keywords: ["cinema", "movie", "theater", "film", "entertainment"], component: CinemaOld },

  // Food & Drink
  {
    name: "Cutlery",
    keywords: ["restaurant", "dining", "food", "eat", "utensils", "fork", "knife"],
    component: Cutlery,
  },
  { name: "PizzaSlice", keywords: ["food", "catering", "pizza", "lunch", "dinner"], component: PizzaSlice },
  { name: "BreadSlice", keywords: ["bread", "bakery", "food", "breakfast", "toast"], component: BreadSlice },
  { name: "Cookie", keywords: ["cookie", "dessert", "snack", "sweet", "bake"], component: Cookie },
  { name: "HalfCookie", keywords: ["cookie", "snack", "treat", "bite", "dessert"], component: HalfCookie },
  { name: "BirthdayCake", keywords: ["cake", "birthday", "celebration", "dessert", "party"], component: BirthdayCake },
  { name: "Fish", keywords: ["fish", "seafood", "food", "sushi", "ocean"], component: Fish },
  {
    name: "OrganicFood",
    keywords: ["organic", "healthy", "vegan", "vegetarian", "diet", "food"],
    component: OrganicFood,
  },
  {
    name: "OrganicFoodSquare",
    keywords: ["organic", "natural", "healthy", "vegan", "vegetarian", "diet"],
    component: OrganicFoodSquare,
  },
  { name: "Vegan", keywords: ["vegan", "plant based", "vegetarian", "diet", "healthy"], component: Vegan },
  { name: "VeganCircle", keywords: ["vegan", "plant based", "diet", "leaf", "organic"], component: VeganCircle },
  { name: "GlassHalf", keywords: ["drink", "beverage", "glass", "wine", "water", "bar"], component: GlassHalf },
  {
    name: "GlassHalfAlt",
    keywords: ["glass", "wine", "stemware", "drink", "bar", "pour", "beverage"],
    component: GlassHalfAlt,
  },
  { name: "GlassEmpty", keywords: ["glass", "empty", "drink", "water", "cup"], component: GlassEmpty },
  {
    name: "GlassFragile",
    keywords: ["wine", "cocktail", "martini", "stemware", "bar", "fragile", "delicate"],
    component: GlassFragile,
  },
  {
    name: "LucideWine",
    keywords: ["wine", "red wine", "white wine", "glass", "bar", "stemware", "vino"],
    component: LucideWine,
  },
  {
    name: "LucideBottleWine",
    keywords: ["wine", "bottle", "vino", "vineyard", "champagne", "bar"],
    component: LucideBottleWine,
  },
  {
    name: "LucideMartini",
    keywords: ["martini", "cocktail", "bar", "drink", "olive", "happy hour"],
    component: LucideMartini,
  },
  {
    name: "LucideBeer",
    keywords: ["beer", "pint", "mug", "pub", "brewery", "ale", "lager", "draft"],
    component: LucideBeer,
  },
  {
    name: "LucideGlassWater",
    keywords: ["water", "glass", "drink", "hydration", "beverage", "cup"],
    component: LucideGlassWater,
  },
  {
    name: "LucideCupSoda",
    keywords: ["soda", "cup", "drink", "soft drink", "fountain", "beverage", "straw"],
    component: LucideCupSoda,
  },
  { name: "Apple", keywords: ["apple", "fruit", "snack", "healthy", "food"], component: Apple },
  { name: "OrangeSlice", keywords: ["orange", "fruit", "citrus", "snack", "food"], component: OrangeSlice },
  { name: "OrangeSliceAlt", keywords: ["orange", "citrus", "fruit", "slice", "snack"], component: OrangeSliceAlt },
  { name: "Egg", keywords: ["egg", "breakfast", "food", "protein", "brunch"], component: Egg },
  { name: "IceCream", keywords: ["ice cream", "dessert", "treat", "sweet", "summer"], component: IceCream },
  { name: "LucideCake", keywords: ["cake", "dessert", "sweet", "celebration", "bakery"], component: LucideCake },
  { name: "LucideCakeSlice", keywords: ["cake", "slice", "dessert", "sweet", "treat"], component: LucideCakeSlice },
  { name: "LucidePizza", keywords: ["pizza", "food", "italian", "lunch", "dinner"], component: LucidePizza },
  { name: "LucideDonut", keywords: ["donut", "doughnut", "dessert", "sweet", "snack"], component: LucideDonut },
  {
    name: "LucideSandwich",
    keywords: ["sandwich", "lunch", "deli", "bread", "food"],
    component: LucideSandwich,
  },
  { name: "LucideSalad", keywords: ["salad", "healthy", "greens", "vegan", "lunch"], component: LucideSalad },
  { name: "LucideSoup", keywords: ["soup", "bowl", "warm", "lunch", "broth", "ramen"], component: LucideSoup },
  {
    name: "LucideHamburger",
    keywords: ["hamburger", "burger", "fast food", "lunch", "dinner"],
    component: LucideHamburger,
  },
  {
    name: "LucidePopcorn",
    keywords: ["popcorn", "movie", "snack", "cinema", "theater"],
    component: LucidePopcorn,
  },
  {
    name: "LucideDrumstick",
    keywords: ["chicken", "drumstick", "poultry", "meat", "dinner"],
    component: LucideDrumstick,
  },
  {
    name: "LucideBeef",
    keywords: ["beef", "steak", "meat", "grill", "bbq", "dinner"],
    component: LucideBeef,
  },
  {
    name: "LucideCroissant",
    keywords: ["croissant", "bakery", "pastry", "breakfast", "french"],
    component: LucideCroissant,
  },
  {
    name: "LucideChefHat",
    keywords: ["chef", "hat", "cook", "kitchen", "restaurant", "catering"],
    component: LucideChefHat,
  },
  {
    name: "LucideUtensils",
    keywords: ["utensils", "fork", "knife", "dining", "restaurant", "eat"],
    component: LucideUtensils,
  },
  {
    name: "LucideUtensilsCrossed",
    keywords: ["dining", "restaurant", "fork", "knife", "meal", "food"],
    component: LucideUtensilsCrossed,
  },
  {
    name: "LucideCandy",
    keywords: ["candy", "sweet", "treat", "lollipop", "snack"],
    component: LucideCandy,
  },

  // Data
  { name: "BubbleStar", keywords: ["review", "feedback", "rating", "star", "quality"], component: BubbleStar },
  { name: "Hashtag", keywords: ["tag", "social", "hashtag", "trending", "topic"], component: Hashtag },
  { name: "Pin", keywords: ["pin", "save", "bookmark", "important"], component: Pin },

  // Documents & Rules
  { name: "Page", keywords: ["document", "page", "file", "paper"], component: Page },
  { name: "PageEdit", keywords: ["document", "edit", "draft", "write", "form"], component: PageEdit },
  { name: "DocStar", keywords: ["document", "important", "featured", "starred"], component: DocStar },
  { name: "Notes", keywords: ["notes", "memo", "write", "jot"], component: Notes },
  { name: "Journal", keywords: ["journal", "diary", "log", "record"], component: Journal },
  { name: "Book", keywords: ["book", "read", "guide", "manual", "handbook"], component: Book },
  { name: "BookmarkBook", keywords: ["bookmark", "saved", "book", "reading"], component: BookmarkBook },
  { name: "BookStack", keywords: ["books", "library", "resources", "collection"], component: BookStack },
  { name: "Folder", keywords: ["folder", "organize", "files", "directory"], component: Folder },
  { name: "Archive", keywords: ["archive", "storage", "backup", "history"], component: Archive },
  {
    name: "ClipboardCheck",
    keywords: ["checklist", "clipboard", "tasks", "compliance", "audit"],
    component: ClipboardCheck,
  },
  { name: "CheckCircle", keywords: ["approved", "verified", "check", "done", "complete"], component: CheckCircle },
  { name: "List", keywords: ["list", "rules", "items", "bullet", "checklist"], component: List },
  { name: "Reports", keywords: ["report", "document", "summary", "analysis"], component: Reports },
  { name: "Ruler", keywords: ["rules", "measure", "guidelines", "standards", "policy"], component: Ruler },
  { name: "EditPencil", keywords: ["edit", "write", "pencil", "compose", "draft"], component: EditPencil },

  // Nature
  { name: "Leaf", keywords: ["nature", "eco", "green", "leaf", "sustainability"], component: Leaf },
  { name: "SunLight", keywords: ["sun", "weather", "bright", "outdoor", "day"], component: SunLight },
  { name: "HalfMoon", keywords: ["moon", "night", "evening", "dark", "weather"], component: HalfMoon },
  { name: "CloudSunny", keywords: ["partly cloudy", "weather", "sun", "cloud", "forecast"], component: CloudSunny },
  { name: "Rain", keywords: ["rain", "weather", "shower", "storm", "wet"], component: Rain },
  { name: "SnowFlake", keywords: ["snow", "winter", "cold", "weather", "flake"], component: SnowFlake },
  { name: "SeaWaves", keywords: ["sea", "ocean", "waves", "beach", "water"], component: SeaWaves },
  { name: "Trekking", keywords: ["outdoor", "adventure", "mountain", "nature", "hike"], component: Trekking },
  { name: "Tree", keywords: ["tree", "nature", "park", "forest", "outdoor"], component: Tree },
  { name: "Flower", keywords: ["flower", "garden", "bloom", "spring", "nature"], component: Flower },
  {
    name: "LucideMountain",
    keywords: ["mountain", "outdoor", "nature", "hike", "peak"],
    component: LucideMountain,
  },
  {
    name: "LucideMountainSnow",
    keywords: ["mountain", "snow", "winter", "ski", "alps", "peak"],
    component: LucideMountainSnow,
  },
  {
    name: "LucideTreePine",
    keywords: ["pine", "tree", "evergreen", "forest", "winter", "holiday"],
    component: LucideTreePine,
  },
  {
    name: "LucideTreeDeciduous",
    keywords: ["tree", "deciduous", "park", "nature", "outdoor"],
    component: LucideTreeDeciduous,
  },
  {
    name: "LucideSunrise",
    keywords: ["sunrise", "morning", "dawn", "early", "scenic"],
    component: LucideSunrise,
  },
  {
    name: "LucideSunset",
    keywords: ["sunset", "evening", "dusk", "scenic"],
    component: LucideSunset,
  },
  {
    name: "LucideMoonStar",
    keywords: ["moon", "star", "night", "evening", "sleep"],
    component: LucideMoonStar,
  },
  {
    name: "LucideFlame",
    keywords: ["fire", "flame", "hot", "trending", "popular"],
    component: LucideFlame,
  },

  // Animals
  { name: "LucideDog", keywords: ["dog", "pet", "puppy", "animal", "canine"], component: LucideDog },
  { name: "LucideCat", keywords: ["cat", "pet", "kitten", "animal", "feline"], component: LucideCat },
  { name: "LucideBird", keywords: ["bird", "animal", "fly", "nature", "wildlife"], component: LucideBird },
  {
    name: "LucideRabbit",
    keywords: ["rabbit", "bunny", "animal", "easter", "pet"],
    component: LucideRabbit,
  },
  {
    name: "LucideTurtle",
    keywords: ["turtle", "slow", "animal", "reptile", "tortoise"],
    component: LucideTurtle,
  },
  {
    name: "LucideSquirrel",
    keywords: ["squirrel", "animal", "rodent", "nuts", "wildlife"],
    component: LucideSquirrel,
  },

  // Misc
  { name: "Home", keywords: ["home", "house", "residence", "venue", "accommodation"], component: Home },
  { name: "Heart", keywords: ["love", "favorite", "heart", "like", "wellness"], component: Heart },
  {
    name: "HeartArrowDown",
    keywords: ["save", "favorite", "like", "love", "bookmark"],
    component: HeartArrowDown,
  },
  { name: "Star", keywords: ["star", "favorite", "featured", "highlight", "top"], component: Star },
  { name: "Bonfire", keywords: ["bonfire", "campfire", "fire", "outdoor", "evening"], component: Bonfire },
  { name: "Hat", keywords: ["hat", "cap", "costume", "fashion", "party"], component: Hat },
  { name: "Glasses", keywords: ["glasses", "spectacles", "eyewear", "fashion", "style"], component: Glasses },
  { name: "Trophy", keywords: ["award", "winner", "trophy", "achievement", "prize"], component: Trophy },
  { name: "Bookmark", keywords: ["save", "bookmark", "favorite", "mark"], component: Bookmark },
  { name: "Clock", keywords: ["time", "clock", "schedule", "deadline", "hours"], component: Clock },
  { name: "Timer", keywords: ["timer", "stopwatch", "countdown", "elapsed", "duration"], component: Timer },
  { name: "Hourglass", keywords: ["hourglass", "wait", "time", "deadline", "pending"], component: Hourglass },
  { name: "TimeZone", keywords: ["time zone", "timezone", "world", "global", "schedule"], component: TimeZone },
  { name: "Key", keywords: ["access", "key", "unlock", "solution", "important"], component: Key },
  { name: "CoffeeCup", keywords: ["coffee", "break", "drink", "cafe", "cup"], component: CoffeeCup },
  {
    name: "LucideAward",
    keywords: ["award", "ribbon", "recognition", "winner", "achievement"],
    component: LucideAward,
  },
  {
    name: "LucideHeartHandshake",
    keywords: ["partnership", "deal", "handshake", "love", "agreement", "trust"],
    component: LucideHeartHandshake,
  },
  {
    name: "LucideHeartPulse",
    keywords: ["wellness", "health", "pulse", "heartbeat", "fitness", "medical"],
    component: LucideHeartPulse,
  },
  {
    name: "LucideGraduationCap",
    keywords: ["graduation", "school", "education", "learn", "academic", "student"],
    component: LucideGraduationCap,
  },
  // Emotions
  {
    name: "LucideSmile",
    keywords: ["smile", "happy", "face", "joy", "satisfied"],
    component: LucideSmile,
  },
  {
    name: "LucideLaugh",
    keywords: ["laugh", "happy", "joy", "lol", "fun"],
    component: LucideLaugh,
  },
  // Hands / hospitality
  {
    name: "LucideHandshake",
    keywords: ["handshake", "deal", "partnership", "agreement", "meeting"],
    component: LucideHandshake,
  },
  {
    name: "LucideHandHeart",
    keywords: ["donate", "give", "love", "charity", "care", "hand"],
    component: LucideHandHeart,
  },
  {
    name: "LucideHandCoins",
    keywords: ["payment", "tip", "money", "give", "hand", "coins"],
    component: LucideHandCoins,
  },
  {
    name: "LucideHandPlatter",
    keywords: ["serve", "platter", "tray", "hospitality", "catering", "waiter"],
    component: LucideHandPlatter,
  },
  {
    name: "LucideConciergeBell",
    keywords: ["concierge", "bell", "service", "hotel", "ring", "front desk"],
    component: LucideConciergeBell,
  },
  // Wellness / health
  {
    name: "LucideBrain",
    keywords: ["brain", "thinking", "ai", "smart", "mind", "learning"],
    component: LucideBrain,
  },
  {
    name: "LucideStethoscope",
    keywords: ["stethoscope", "doctor", "medical", "health", "checkup"],
    component: LucideStethoscope,
  },
  {
    name: "LucideEye",
    keywords: ["eye", "view", "see", "visibility", "look", "preview"],
    component: LucideEye,
  },
  // Holidays / fun
  {
    name: "LucideGhost",
    keywords: ["ghost", "halloween", "spooky", "boo", "spirit"],
    component: LucideGhost,
  },
  {
    name: "LucideOrigami",
    keywords: ["origami", "paper", "fold", "craft", "art", "japanese"],
    component: LucideOrigami,
  },
  {
    name: "LucideDiamond",
    keywords: ["diamond", "gem", "premium", "valuable", "vip", "luxury"],
    component: LucideDiamond,
  },
  // Creative tools
  {
    name: "LucidePaintbrush",
    keywords: ["paint", "brush", "art", "design", "creative"],
    component: LucidePaintbrush,
  },
  {
    name: "LucideNotepadText",
    keywords: ["notepad", "notes", "list", "memo", "write"],
    component: LucideNotepadText,
  },
  {
    name: "LucideNewspaper",
    keywords: ["newspaper", "news", "article", "press", "publication"],
    component: LucideNewspaper,
  },
  {
    name: "LucideReceipt",
    keywords: ["receipt", "invoice", "bill", "purchase", "transaction"],
    component: LucideReceipt,
  },
  {
    name: "LucidePencil",
    keywords: ["pencil", "edit", "write", "draft", "sketch"],
    component: LucidePencil,
  },
  // Construction / industry
  {
    name: "LucideHardHat",
    keywords: ["hard hat", "construction", "safety", "worker", "build"],
    component: LucideHardHat,
  },
  {
    name: "LucideConstruction",
    keywords: ["construction", "barrier", "work", "cone", "build", "warning"],
    component: LucideConstruction,
  },
  // Apparel
  {
    name: "LucideShirt",
    keywords: ["shirt", "t-shirt", "apparel", "clothes", "swag", "merch"],
    component: LucideShirt,
  },
  {
    name: "LucideWatch",
    keywords: ["watch", "wristwatch", "time", "timepiece", "accessory"],
    component: LucideWatch,
  },
  {
    name: "LucideWheat",
    keywords: ["wheat", "grain", "harvest", "farm", "gluten", "agriculture"],
    component: LucideWheat,
  },
];

/** Fast lookup: icon name → component */
export const ICON_REGISTRY: Record<
  string,
  React.ComponentType<{ className?: string; strokeWidth?: string | number }>
> = Object.fromEntries(CURATED_ICONS.map((i) => [i.name, i.component]));

// ============================================================
// Emoji Data
// ============================================================

interface EmojiEntry {
  emoji: string;
  name: string;
  keywords: string[];
}

/** Curated emoji set — business/event appropriate */
export const CURATED_EMOJIS: EmojiEntry[] = [
  // Business
  { emoji: "🏢", name: "Office Building", keywords: ["office", "corporate", "company", "building"] },
  { emoji: "💼", name: "Briefcase", keywords: ["work", "job", "business", "professional"] },
  { emoji: "📊", name: "Bar Chart", keywords: ["data", "analytics", "chart", "stats"] },
  { emoji: "💰", name: "Money Bag", keywords: ["money", "budget", "cost", "finance"] },
  { emoji: "🏦", name: "Bank", keywords: ["bank", "finance", "institution"] },
  { emoji: "📈", name: "Chart Up", keywords: ["growth", "increase", "trending", "progress"] },
  { emoji: "💳", name: "Credit Card", keywords: ["payment", "card", "purchase"] },

  // People
  { emoji: "👥", name: "People", keywords: ["team", "group", "people", "users"] },
  { emoji: "🧑‍🤝‍🧑", name: "People Holding Hands", keywords: ["together", "group", "couple", "community"] },
  { emoji: "👨‍👩‍👧", name: "Family", keywords: ["family", "parents", "kids", "group"] },
  { emoji: "👨‍👩‍👧‍👦", name: "Large Family", keywords: ["family", "kids", "household", "group"] },
  { emoji: "👫", name: "Couple", keywords: ["couple", "two", "partners", "duo"] },
  { emoji: "👬", name: "Two Men", keywords: ["men", "friends", "buddies", "duo"] },
  { emoji: "👭", name: "Two Women", keywords: ["women", "friends", "duo", "pair"] },
  { emoji: "🤝", name: "Handshake", keywords: ["deal", "partnership", "agreement", "meeting"] },
  { emoji: "👋", name: "Wave", keywords: ["hello", "greeting", "welcome", "hi"] },
  { emoji: "🙌", name: "Raised Hands", keywords: ["celebrate", "success", "hooray", "praise"] },
  { emoji: "💪", name: "Flexed Bicep", keywords: ["strong", "power", "strength", "fitness"] },
  { emoji: "🧑‍💻", name: "Technologist", keywords: ["developer", "coder", "tech", "computer"] },
  { emoji: "🧑‍🎓", name: "Student", keywords: ["student", "graduate", "school", "learner"] },
  { emoji: "🧑‍🏫", name: "Teacher", keywords: ["teacher", "instructor", "professor", "speaker"] },
  { emoji: "🧑‍💼", name: "Office Worker", keywords: ["office", "worker", "professional", "business"] },
  { emoji: "🧑‍⚕️", name: "Health Worker", keywords: ["doctor", "nurse", "medical", "health"] },
  { emoji: "🧑‍🔧", name: "Mechanic", keywords: ["mechanic", "engineer", "fix", "repair"] },
  { emoji: "🧑‍🎤", name: "Singer", keywords: ["singer", "performer", "artist", "musician"] },
  { emoji: "🧑‍🎨", name: "Artist", keywords: ["artist", "designer", "creative", "painter"] },
  { emoji: "👶", name: "Baby", keywords: ["baby", "infant", "child", "newborn"] },
  { emoji: "🧒", name: "Child", keywords: ["child", "kid", "youth"] },
  { emoji: "🧓", name: "Older Adult", keywords: ["senior", "older", "elder", "retired"] },
  { emoji: "👑", name: "Crown", keywords: ["leader", "vip", "premium", "king", "queen"] },

  // Events
  { emoji: "🎉", name: "Party Popper", keywords: ["celebration", "party", "event", "congrats"] },
  { emoji: "🎊", name: "Confetti Ball", keywords: ["celebration", "festive", "party"] },
  { emoji: "🎯", name: "Bullseye", keywords: ["goal", "target", "focus", "aim"] },
  { emoji: "🎪", name: "Circus Tent", keywords: ["event", "show", "entertainment", "venue"] },
  { emoji: "🎤", name: "Microphone", keywords: ["speaker", "presentation", "talk", "mic"] },
  { emoji: "🎙️", name: "Studio Microphone", keywords: ["podcast", "studio", "interview", "broadcast"] },
  { emoji: "🎵", name: "Musical Notes", keywords: ["music", "entertainment", "concert"] },
  { emoji: "🎬", name: "Clapperboard", keywords: ["film", "video", "production", "media"] },
  { emoji: "🎫", name: "Ticket", keywords: ["ticket", "admission", "entry", "pass"] },
  { emoji: "🎟️", name: "Admission Tickets", keywords: ["tickets", "event", "entry", "admission"] },
  { emoji: "🪅", name: "Piñata", keywords: ["party", "celebration", "fiesta", "fun"] },
  { emoji: "🎀", name: "Ribbon", keywords: ["ribbon", "decoration", "gift", "bow"] },
  { emoji: "🏵️", name: "Rosette", keywords: ["rosette", "award", "ribbon", "winner"] },
  { emoji: "🎖️", name: "Military Medal", keywords: ["medal", "honor", "award", "recognition"] },
  { emoji: "🥇", name: "Gold Medal", keywords: ["winner", "first", "gold", "medal", "award"] },
  { emoji: "🥈", name: "Silver Medal", keywords: ["runner up", "second", "silver", "medal"] },
  { emoji: "🥉", name: "Bronze Medal", keywords: ["third", "bronze", "medal", "award"] },
  { emoji: "🪩", name: "Disco Ball", keywords: ["party", "disco", "dance", "club", "night"] },
  { emoji: "🎺", name: "Trumpet", keywords: ["trumpet", "music", "band", "brass"] },
  { emoji: "🎷", name: "Saxophone", keywords: ["sax", "music", "jazz", "band"] },
  { emoji: "🎸", name: "Guitar", keywords: ["guitar", "music", "rock", "band"] },
  { emoji: "🥁", name: "Drum", keywords: ["drums", "music", "band", "percussion"] },
  { emoji: "🪗", name: "Accordion", keywords: ["accordion", "music", "folk", "band"] },
  { emoji: "🎶", name: "Musical Notes", keywords: ["music", "song", "tune", "melody", "notes", "party"] },
  { emoji: "🎼", name: "Musical Score", keywords: ["sheet music", "score", "composition", "song", "music"] },
  { emoji: "🎹", name: "Piano", keywords: ["piano", "keyboard", "music", "keys", "instrument"] },
  { emoji: "🎻", name: "Violin", keywords: ["violin", "fiddle", "music", "strings", "classical"] },
  { emoji: "🪕", name: "Banjo", keywords: ["banjo", "music", "strings", "folk", "country"] },
  { emoji: "🎭", name: "Performing Arts", keywords: ["theater", "drama", "masks", "performance", "show", "play"] },
  { emoji: "🥳", name: "Partying Face", keywords: ["party", "celebrate", "birthday", "hat", "fun", "horn"] },
  { emoji: "🤩", name: "Star Struck", keywords: ["wow", "star", "amazed", "excited", "celebrity", "fan"] },
  { emoji: "😎", name: "Cool Face", keywords: ["cool", "sunglasses", "chill", "swag", "vibe"] },
  { emoji: "🤹", name: "Person Juggling", keywords: ["juggling", "circus", "performer", "entertainer", "show"] },
  { emoji: "💃", name: "Woman Dancing", keywords: ["dance", "salsa", "party", "performance", "celebrate"] },
  { emoji: "🕺", name: "Man Dancing", keywords: ["dance", "party", "disco", "fun", "celebrate"] },
  { emoji: "👯", name: "People with Bunny Ears", keywords: ["dancing", "duo", "party", "fun", "perform"] },
  { emoji: "🎆", name: "Fireworks", keywords: ["fireworks", "celebrate", "new year", "festival", "spark"] },
  { emoji: "🎇", name: "Sparkler", keywords: ["sparkler", "spark", "fireworks", "celebrate", "fourth of july"] },
  { emoji: "🧨", name: "Firecracker", keywords: ["firecracker", "explosive", "celebrate", "new year", "boom"] },
  { emoji: "🎩", name: "Top Hat", keywords: ["hat", "magic", "tuxedo", "formal", "show", "performer"] },
  { emoji: "🪄", name: "Magic Wand", keywords: ["magic", "wand", "spell", "wizard", "ai", "transform"] },
  { emoji: "🪅", name: "Piñata", keywords: ["piñata", "party", "celebration", "fiesta", "fun", "kids"] },
  { emoji: "🎰", name: "Slot Machine", keywords: ["slot machine", "casino", "gamble", "luck", "vegas"] },
  { emoji: "🃏", name: "Joker", keywords: ["joker", "card", "playing card", "poker", "casino", "game"] },
  { emoji: "🎴", name: "Flower Playing Cards", keywords: ["cards", "japanese", "hanafuda", "game"] },
  { emoji: "🀄", name: "Mahjong Tile", keywords: ["mahjong", "game", "tile", "asian", "play"] },
  { emoji: "♠️", name: "Spade Suit", keywords: ["spade", "card", "suit", "poker", "casino"] },
  { emoji: "♥️", name: "Heart Suit", keywords: ["heart", "card", "suit", "poker", "casino"] },
  { emoji: "♦️", name: "Diamond Suit", keywords: ["diamond", "card", "suit", "poker", "casino"] },
  { emoji: "♣️", name: "Club Suit", keywords: ["club", "card", "suit", "poker", "casino"] },
  { emoji: "🤵", name: "Person in Tuxedo", keywords: ["tuxedo", "formal", "wedding", "groom", "black tie"] },
  { emoji: "👰", name: "Person with Veil", keywords: ["bride", "wedding", "veil", "marriage", "ceremony"] },
  { emoji: "💏", name: "Kiss", keywords: ["kiss", "couple", "romance", "love", "wedding"] },
  { emoji: "💍", name: "Ring", keywords: ["ring", "engagement", "wedding", "jewelry", "marriage"] },
  { emoji: "🦄", name: "Unicorn", keywords: ["unicorn", "magical", "fantasy", "fun", "party"] },
  { emoji: "🧧", name: "Red Envelope", keywords: ["gift", "money", "reward", "bonus", "envelope"] },

  // Travel
  { emoji: "✈️", name: "Airplane", keywords: ["flight", "travel", "trip", "international"] },
  { emoji: "🛩️", name: "Small Airplane", keywords: ["private jet", "small plane", "flight"] },
  { emoji: "🚁", name: "Helicopter", keywords: ["helicopter", "chopper", "flight", "transport"] },
  { emoji: "🛳️", name: "Cruise Ship", keywords: ["cruise", "ship", "ocean", "vacation", "voyage"] },
  { emoji: "⛵", name: "Sailboat", keywords: ["sailboat", "sail", "boat", "ocean", "yacht"] },
  { emoji: "🚤", name: "Speedboat", keywords: ["speedboat", "boat", "water", "transport"] },
  { emoji: "⚓", name: "Anchor", keywords: ["anchor", "boat", "ship", "marine", "port"] },
  { emoji: "🌍", name: "Globe", keywords: ["world", "global", "international", "earth"] },
  { emoji: "📍", name: "Location Pin", keywords: ["location", "place", "pin", "address"] },
  { emoji: "🚀", name: "Rocket", keywords: ["launch", "startup", "fast", "growth"] },
  { emoji: "🗺️", name: "World Map", keywords: ["map", "travel", "explore", "navigate"] },
  { emoji: "🏨", name: "Hotel", keywords: ["hotel", "accommodation", "stay", "venue"] },
  { emoji: "🏖️", name: "Beach", keywords: ["beach", "vacation", "umbrella", "sand", "ocean"] },
  { emoji: "🏝️", name: "Desert Island", keywords: ["island", "tropical", "vacation", "beach"] },
  { emoji: "🏔️", name: "Snowy Mountain", keywords: ["mountain", "snow", "winter", "alps", "ski"] },
  { emoji: "🏞️", name: "National Park", keywords: ["park", "nature", "outdoor", "scenic"] },
  { emoji: "🏟️", name: "Stadium", keywords: ["stadium", "sports", "venue", "arena"] },
  { emoji: "🏛️", name: "Classical Building", keywords: ["museum", "monument", "landmark", "history"] },
  { emoji: "🏰", name: "Castle", keywords: ["castle", "palace", "landmark", "history"] },
  { emoji: "🌅", name: "Sunrise", keywords: ["sunrise", "morning", "dawn", "scenic"] },
  { emoji: "🌇", name: "Sunset", keywords: ["sunset", "evening", "dusk", "scenic"] },
  { emoji: "🏙️", name: "Cityscape", keywords: ["city", "skyline", "urban", "downtown"] },
  { emoji: "🌃", name: "City at Night", keywords: ["nightlife", "city", "evening", "skyline"] },
  { emoji: "🌉", name: "Bridge at Night", keywords: ["bridge", "landmark", "scenic", "city"] },
  { emoji: "🚗", name: "Car", keywords: ["drive", "transport", "ride", "vehicle"] },
  { emoji: "🚌", name: "Bus", keywords: ["bus", "transit", "public", "transport", "shuttle"] },
  { emoji: "🚂", name: "Train", keywords: ["train", "rail", "transit", "railway"] },
  { emoji: "🚇", name: "Metro", keywords: ["metro", "subway", "underground", "transit"] },
  { emoji: "🚕", name: "Taxi", keywords: ["taxi", "cab", "ride", "transport"] },
  { emoji: "🏍️", name: "Motorcycle", keywords: ["motorcycle", "motorbike", "scooter", "ride"] },
  { emoji: "🚲", name: "Bicycle", keywords: ["bike", "cycle", "ride", "transport"] },
  { emoji: "🛴", name: "Scooter", keywords: ["scooter", "kick", "ride", "transport"] },
  { emoji: "🚶", name: "Walking", keywords: ["walk", "pedestrian", "stroll", "on foot"] },
  { emoji: "🅿️", name: "Parking", keywords: ["parking", "park", "garage", "car"] },
  { emoji: "⛽", name: "Fuel Pump", keywords: ["gas", "fuel", "petrol", "station"] },
  { emoji: "🧳", name: "Luggage", keywords: ["luggage", "suitcase", "travel", "trip", "bag"] },
  { emoji: "🚐", name: "Minibus", keywords: ["van", "minibus", "shuttle", "transport", "group"] },
  { emoji: "🚙", name: "SUV", keywords: ["suv", "car", "vehicle", "drive", "road trip"] },
  { emoji: "🛥️", name: "Motor Boat", keywords: ["boat", "motorboat", "cruise", "water", "yacht"] },
  { emoji: "🚠", name: "Cable Car", keywords: ["cable car", "gondola", "mountain", "lift", "ski"] },
  { emoji: "🎡", name: "Ferris Wheel", keywords: ["ferris wheel", "fair", "carnival", "amusement", "ride"] },
  { emoji: "🎢", name: "Roller Coaster", keywords: ["roller coaster", "theme park", "amusement", "ride", "fun"] },
  { emoji: "🏜️", name: "Desert", keywords: ["desert", "sand", "dunes", "landscape", "retreat"] },
  { emoji: "🌋", name: "Volcano", keywords: ["volcano", "mountain", "nature", "landscape"] },
  { emoji: "🗻", name: "Mount Fuji", keywords: ["mountain", "peak", "fuji", "summit", "landscape"] },
  { emoji: "🏘️", name: "Houses", keywords: ["houses", "neighborhood", "village", "homes", "community"] },
  { emoji: "🛸", name: "Flying Saucer", keywords: ["ufo", "flying saucer", "alien", "space", "spaceship"] },
  { emoji: "🪐", name: "Ringed Planet", keywords: ["planet", "saturn", "space", "galaxy", "astronomy"] },
  { emoji: "🌌", name: "Milky Way", keywords: ["galaxy", "stars", "space", "night sky", "cosmos"] },

  // Communication
  { emoji: "💬", name: "Speech Bubble", keywords: ["chat", "message", "talk", "discuss"] },
  { emoji: "📧", name: "Email", keywords: ["email", "inbox", "message", "send"] },
  { emoji: "📣", name: "Megaphone", keywords: ["announce", "marketing", "broadcast"] },
  { emoji: "🔔", name: "Bell", keywords: ["notification", "alert", "reminder"] },
  { emoji: "💡", name: "Light Bulb", keywords: ["idea", "insight", "solution", "bright"] },
  { emoji: "📱", name: "Phone", keywords: ["mobile", "device", "app", "call"] },
  { emoji: "📞", name: "Telephone Receiver", keywords: ["call", "phone", "contact", "support"] },
  { emoji: "☎️", name: "Telephone", keywords: ["telephone", "call", "phone", "landline"] },

  // Technology
  { emoji: "💻", name: "Laptop", keywords: ["computer", "laptop", "tech", "device", "work"] },
  { emoji: "🖥️", name: "Desktop Computer", keywords: ["desktop", "computer", "monitor", "tech"] },
  { emoji: "⌨️", name: "Keyboard", keywords: ["keyboard", "typing", "input", "tech"] },
  { emoji: "🖱️", name: "Mouse", keywords: ["mouse", "computer", "cursor", "device"] },
  { emoji: "🖨️", name: "Printer", keywords: ["printer", "print", "office", "document"] },
  { emoji: "📺", name: "Television", keywords: ["tv", "television", "screen", "watch"] },
  { emoji: "📷", name: "Camera", keywords: ["camera", "photo", "picture", "capture"] },
  { emoji: "📹", name: "Video Camera", keywords: ["video", "camera", "recording", "film"] },
  { emoji: "🎥", name: "Movie Camera", keywords: ["movie", "film", "camera", "production"] },
  { emoji: "📽️", name: "Film Projector", keywords: ["projector", "film", "screening", "movie"] },
  { emoji: "🎧", name: "Headphones", keywords: ["headphones", "music", "audio", "listening"] },
  { emoji: "🔊", name: "Speaker", keywords: ["speaker", "audio", "sound", "loud"] },
  { emoji: "📡", name: "Satellite Antenna", keywords: ["satellite", "antenna", "signal", "broadcast"] },
  { emoji: "🛜", name: "Wireless", keywords: ["wifi", "wireless", "internet", "signal"] },
  { emoji: "📶", name: "Antenna Bars", keywords: ["signal", "wifi", "network", "bars"] },
  { emoji: "🔋", name: "Battery", keywords: ["battery", "power", "charge", "energy"] },
  { emoji: "🔌", name: "Plug", keywords: ["plug", "power", "outlet", "electric"] },
  { emoji: "💾", name: "Floppy Disk", keywords: ["save", "disk", "storage", "retro"] },
  { emoji: "💿", name: "Optical Disk", keywords: ["cd", "disc", "media", "storage"] },
  { emoji: "🤖", name: "Robot", keywords: ["robot", "ai", "automation", "bot"] },

  // Objects
  { emoji: "⭐", name: "Star", keywords: ["star", "favorite", "featured", "top"] },
  { emoji: "🏆", name: "Trophy", keywords: ["award", "winner", "achievement", "prize"] },
  { emoji: "📋", name: "Clipboard", keywords: ["list", "checklist", "tasks", "plan"] },
  { emoji: "🔑", name: "Key", keywords: ["access", "key", "unlock", "important"] },
  { emoji: "🎓", name: "Graduation Cap", keywords: ["education", "learning", "school", "graduate"] },
  { emoji: "📝", name: "Memo", keywords: ["note", "write", "document", "draft"] },
  { emoji: "🔒", name: "Lock", keywords: ["secure", "private", "locked", "safe"] },
  { emoji: "⏰", name: "Alarm Clock", keywords: ["time", "clock", "schedule", "deadline"] },
  { emoji: "📦", name: "Package", keywords: ["delivery", "box", "shipping", "product"] },
  { emoji: "🧭", name: "Compass", keywords: ["compass", "navigate", "explore", "direction"] },
  { emoji: "🎒", name: "Backpack", keywords: ["backpack", "travel", "bag", "school"] },
  { emoji: "🛎️", name: "Bellhop Bell", keywords: ["bell", "hotel", "service", "concierge"] },
  { emoji: "🔗", name: "Link", keywords: ["link", "url", "connect", "chain", "reference"] },

  // Documents & Rules
  { emoji: "📄", name: "Document", keywords: ["document", "page", "file", "paper"] },
  { emoji: "📑", name: "Bookmark Tabs", keywords: ["tabs", "bookmark", "document", "organize"] },
  { emoji: "📖", name: "Open Book", keywords: ["book", "read", "guide", "manual"] },
  { emoji: "📚", name: "Books", keywords: ["books", "library", "resources", "collection"] },
  { emoji: "📂", name: "Open Folder", keywords: ["folder", "files", "organize", "directory"] },
  { emoji: "🗂️", name: "Card Index", keywords: ["index", "files", "organize", "archive"] },
  { emoji: "📐", name: "Triangular Ruler", keywords: ["ruler", "rules", "measure", "guidelines"] },
  { emoji: "📏", name: "Straight Ruler", keywords: ["ruler", "measure", "standards", "policy"] },
  { emoji: "✏️", name: "Pencil", keywords: ["edit", "write", "pencil", "draft"] },
  { emoji: "🖊️", name: "Pen", keywords: ["pen", "sign", "write", "contract"] },
  { emoji: "📌", name: "Pushpin", keywords: ["pin", "important", "note", "reminder"] },
  { emoji: "🗒️", name: "Notepad", keywords: ["notepad", "notes", "list", "memo"] },
  { emoji: "✍️", name: "Writing Hand", keywords: ["write", "sign", "signature", "author"] },
  { emoji: "⚖️", name: "Balance Scale", keywords: ["law", "justice", "balance", "legal", "rules"] },
  { emoji: "🔖", name: "Bookmark", keywords: ["bookmark", "save", "tag", "mark"] },

  // Nature
  { emoji: "🌿", name: "Herb", keywords: ["nature", "green", "eco", "plant"] },
  { emoji: "☀️", name: "Sun", keywords: ["sun", "bright", "outdoor", "weather"] },
  { emoji: "🌊", name: "Wave", keywords: ["ocean", "water", "beach", "sea"] },
  { emoji: "🌸", name: "Cherry Blossom", keywords: ["flower", "spring", "bloom", "nature"] },
  { emoji: "🍃", name: "Leaves", keywords: ["leaf", "nature", "wind", "eco"] },
  { emoji: "⛰️", name: "Mountain", keywords: ["mountain", "outdoor", "nature", "hike"] },

  // Alerts
  { emoji: "⚠️", name: "Warning", keywords: ["warning", "caution", "alert", "danger"] },
  { emoji: "🚨", name: "Rotating Light", keywords: ["emergency", "alert", "warning", "urgent"] },
  { emoji: "❗", name: "Exclamation", keywords: ["important", "alert", "attention", "caution"] },
  { emoji: "ℹ️", name: "Information", keywords: ["info", "information", "help", "notice"] },
  { emoji: "❓", name: "Question Mark", keywords: ["question", "ask", "faq", "help", "unknown"] },
  { emoji: "💭", name: "Thought Bubble", keywords: ["thinking", "question", "wonder", "idea"] },
  { emoji: "🛂", name: "Passport Control", keywords: ["passport", "border", "customs", "travel", "id", "visa"] },

  // Symbols
  { emoji: "✅", name: "Check Mark", keywords: ["done", "complete", "yes", "check", "approved"] },
  { emoji: "❤️", name: "Red Heart", keywords: ["love", "favorite", "heart", "like"] },
  { emoji: "🔥", name: "Fire", keywords: ["hot", "trending", "popular", "fire", "campfire", "bonfire", "flame"] },
  { emoji: "⚡", name: "Lightning", keywords: ["fast", "energy", "power", "electric"] },
  { emoji: "🎈", name: "Balloon", keywords: ["party", "celebration", "fun", "event"] },
  { emoji: "💎", name: "Gem", keywords: ["premium", "valuable", "quality", "diamond"] },
  { emoji: "🎁", name: "Gift", keywords: ["present", "gift", "surprise", "reward"] },

  // Food & Drink
  { emoji: "🍽️", name: "Plate with Cutlery", keywords: ["dining", "restaurant", "meal", "food", "eat", "dinner"] },
  { emoji: "🍕", name: "Pizza", keywords: ["food", "catering", "pizza", "lunch", "dinner"] },
  { emoji: "🍔", name: "Hamburger", keywords: ["burger", "food", "fast food", "lunch", "restaurant"] },
  { emoji: "🌭", name: "Hot Dog", keywords: ["hot dog", "fast food", "snack", "lunch"] },
  { emoji: "🥪", name: "Sandwich", keywords: ["sandwich", "lunch", "deli", "bread"] },
  { emoji: "🌮", name: "Taco", keywords: ["taco", "mexican", "food", "lunch", "dinner"] },
  { emoji: "🌯", name: "Burrito", keywords: ["burrito", "mexican", "food", "wrap"] },
  { emoji: "🥗", name: "Salad", keywords: ["salad", "healthy", "food", "vegan", "vegetarian"] },
  { emoji: "🍣", name: "Sushi", keywords: ["sushi", "japanese", "food", "seafood", "restaurant"] },
  { emoji: "🍱", name: "Bento Box", keywords: ["bento", "japanese", "lunch", "meal"] },
  { emoji: "🍙", name: "Rice Ball", keywords: ["onigiri", "rice ball", "japanese", "snack"] },
  { emoji: "🍚", name: "Cooked Rice", keywords: ["rice", "bowl", "asian", "meal"] },
  { emoji: "🍜", name: "Steaming Bowl", keywords: ["ramen", "noodles", "soup", "asian"] },
  { emoji: "🍝", name: "Spaghetti", keywords: ["pasta", "spaghetti", "italian", "dinner"] },
  { emoji: "🥟", name: "Dumpling", keywords: ["dumpling", "asian", "food", "appetizer"] },
  { emoji: "🍳", name: "Cooking", keywords: ["egg", "breakfast", "cooking", "brunch"] },
  { emoji: "🥞", name: "Pancakes", keywords: ["pancakes", "breakfast", "brunch", "stack"] },
  { emoji: "🧇", name: "Waffle", keywords: ["waffle", "breakfast", "brunch", "sweet"] },
  { emoji: "🥯", name: "Bagel", keywords: ["bagel", "bread", "breakfast", "brunch"] },
  { emoji: "🥨", name: "Pretzel", keywords: ["pretzel", "snack", "bread", "salty"] },
  { emoji: "🥐", name: "Croissant", keywords: ["bread", "bakery", "pastry", "breakfast", "french"] },
  { emoji: "🍰", name: "Cake", keywords: ["cake", "dessert", "sweet", "birthday", "celebration"] },
  { emoji: "🎂", name: "Birthday Cake", keywords: ["birthday", "celebration", "cake", "candles"] },
  { emoji: "🍪", name: "Cookie", keywords: ["cookie", "dessert", "snack", "sweet", "bake"] },
  { emoji: "🍩", name: "Doughnut", keywords: ["donut", "dessert", "sweet", "snack", "breakfast"] },
  { emoji: "🍦", name: "Ice Cream", keywords: ["ice cream", "dessert", "summer", "sweet"] },
  { emoji: "🍨", name: "Ice Cream Sundae", keywords: ["sundae", "dessert", "ice cream", "treat"] },
  { emoji: "🍫", name: "Chocolate Bar", keywords: ["chocolate", "candy", "sweet", "snack"] },
  { emoji: "🍬", name: "Candy", keywords: ["candy", "sweet", "treat", "snack"] },
  { emoji: "🥩", name: "Steak", keywords: ["meat", "steak", "food", "dinner", "grill", "bbq"] },
  { emoji: "🍗", name: "Chicken Leg", keywords: ["chicken", "meat", "poultry", "dinner"] },
  { emoji: "🥓", name: "Bacon", keywords: ["bacon", "breakfast", "meat", "brunch"] },
  { emoji: "🐟", name: "Fish", keywords: ["fish", "seafood", "food", "sushi", "ocean"] },
  { emoji: "🦞", name: "Lobster", keywords: ["lobster", "seafood", "shellfish", "dinner"] },
  { emoji: "🦀", name: "Crab", keywords: ["crab", "seafood", "shellfish", "ocean"] },
  { emoji: "🥬", name: "Leafy Green", keywords: ["vegetable", "healthy", "vegan", "organic", "food"] },
  { emoji: "🥑", name: "Avocado", keywords: ["avocado", "healthy", "brunch", "vegan"] },
  { emoji: "🍅", name: "Tomato", keywords: ["tomato", "vegetable", "fresh", "cooking"] },
  { emoji: "🥕", name: "Carrot", keywords: ["carrot", "vegetable", "healthy", "snack"] },
  { emoji: "🍎", name: "Apple", keywords: ["apple", "fruit", "snack", "healthy"] },
  { emoji: "🍌", name: "Banana", keywords: ["banana", "fruit", "snack", "healthy"] },
  { emoji: "🍇", name: "Grapes", keywords: ["grapes", "fruit", "wine", "snack"] },
  { emoji: "🍊", name: "Tangerine", keywords: ["orange", "fruit", "citrus", "healthy"] },
  { emoji: "🍋", name: "Lemon", keywords: ["lemon", "citrus", "fresh", "sour"] },
  { emoji: "🍓", name: "Strawberry", keywords: ["strawberry", "berry", "fruit", "sweet"] },
  { emoji: "🍉", name: "Watermelon", keywords: ["watermelon", "fruit", "summer", "refreshing"] },
  { emoji: "🍍", name: "Pineapple", keywords: ["pineapple", "fruit", "tropical", "hawaiian"] },
  { emoji: "🥥", name: "Coconut", keywords: ["coconut", "tropical", "fruit", "beach"] },
  { emoji: "🧁", name: "Cupcake", keywords: ["cupcake", "dessert", "sweet", "bakery", "treat"] },
  {
    emoji: "🍷",
    name: "Wine Glass",
    keywords: ["wine", "red", "white", "drink", "bar", "restaurant", "toast", "stemware"],
  },
  { emoji: "🥂", name: "Clinking Glasses", keywords: ["champagne", "cheers", "toast", "celebration", "flute"] },
  { emoji: "🍾", name: "Champagne", keywords: ["champagne", "celebration", "bottle", "party", "popping"] },
  { emoji: "🍸", name: "Cocktail", keywords: ["cocktail", "martini", "bar", "drink", "mixology"] },
  { emoji: "🥃", name: "Whiskey", keywords: ["whiskey", "tumbler", "bar", "drink", "scotch", "bourbon", "rocks"] },
  { emoji: "🍺", name: "Beer Mug", keywords: ["beer", "pint", "drink", "bar", "pub", "brewery", "ale", "lager"] },
  { emoji: "🍻", name: "Clinking Beer Mugs", keywords: ["cheers", "beer", "pint", "toast", "social", "pub"] },
  { emoji: "🍹", name: "Tropical Drink", keywords: ["cocktail", "drink", "bar", "tropical", "party", "tiki"] },
  { emoji: "🍶", name: "Sake", keywords: ["sake", "japanese", "drink", "rice wine"] },
  { emoji: "🫗", name: "Pouring Liquid", keywords: ["pour", "drink", "beverage", "spill", "glass"] },
  { emoji: "🫖", name: "Teapot", keywords: ["teapot", "tea", "pot", "kettle", "brew"] },
  { emoji: "🫙", name: "Jar", keywords: ["jar", "container", "preserve", "mason"] },
  { emoji: "🍯", name: "Honey Pot", keywords: ["honey", "sweet", "jar", "pot"] },
  { emoji: "☕", name: "Coffee", keywords: ["coffee", "break", "cafe", "drink", "espresso"] },
  { emoji: "🍵", name: "Tea", keywords: ["tea", "matcha", "drink", "cup", "asian"] },
  { emoji: "🧋", name: "Bubble Tea", keywords: ["boba", "bubble tea", "drink", "asian"] },
  { emoji: "🥤", name: "Cup with Straw", keywords: ["soda", "drink", "beverage", "soft drink"] },
  { emoji: "🧃", name: "Juice Box", keywords: ["juice", "drink", "beverage", "kids", "refreshment"] },
  { emoji: "🧊", name: "Ice Cube", keywords: ["ice", "cold", "drink", "frozen"] },
  { emoji: "👨‍🍳", name: "Chef", keywords: ["chef", "cook", "kitchen", "restaurant", "food"] },
  { emoji: "🥘", name: "Pan of Food", keywords: ["pan", "paella", "cooking", "dinner", "meal"] },
  { emoji: "🍲", name: "Pot of Food", keywords: ["stew", "soup", "pot", "hot", "meal"] },
  { emoji: "🫕", name: "Fondue", keywords: ["fondue", "cheese", "melt", "sharing", "dinner"] },
  { emoji: "🧀", name: "Cheese", keywords: ["cheese", "dairy", "snack", "charcuterie"] },
  { emoji: "🥖", name: "Baguette", keywords: ["bread", "baguette", "bakery", "french"] },
  { emoji: "🌽", name: "Corn", keywords: ["corn", "vegetable", "bbq", "cob"] },
  { emoji: "🍑", name: "Peach", keywords: ["peach", "fruit", "fresh"] },
  { emoji: "🍒", name: "Cherries", keywords: ["cherries", "fruit", "fresh"] },
  { emoji: "🥝", name: "Kiwi", keywords: ["kiwi", "fruit", "fresh", "tropical"] },
  { emoji: "🍤", name: "Fried Shrimp", keywords: ["shrimp", "seafood", "appetizer", "fried"] },

  // Activities & Sports
  { emoji: "🏃", name: "Running", keywords: ["run", "running", "jog", "sport", "exercise"] },
  { emoji: "🥾", name: "Hiking", keywords: ["hike", "boot", "trail", "outdoor", "trek"] },
  { emoji: "🧗", name: "Climbing", keywords: ["climb", "rock climbing", "bouldering", "sport"] },
  { emoji: "🏊", name: "Swimming", keywords: ["swim", "pool", "water", "sport", "activity"] },
  { emoji: "🧘", name: "Yoga", keywords: ["yoga", "meditation", "wellness", "stretch", "mindfulness"] },
  { emoji: "🏋️", name: "Weight Lifting", keywords: ["gym", "weight", "lifting", "strength", "fitness"] },
  { emoji: "🤸", name: "Cartwheel", keywords: ["gymnastics", "tumble", "exercise", "active"] },
  { emoji: "💃", name: "Dancer", keywords: ["dance", "salsa", "party", "performance"] },
  { emoji: "🕺", name: "Man Dancing", keywords: ["dance", "party", "disco", "fun"] },
  { emoji: "⛳", name: "Golf", keywords: ["golf", "sport", "club", "course", "activity"] },
  { emoji: "🎾", name: "Tennis", keywords: ["tennis", "sport", "racket", "ball", "activity"] },
  { emoji: "⚽", name: "Soccer Ball", keywords: ["soccer", "football", "sport", "game"] },
  { emoji: "🏀", name: "Basketball", keywords: ["basketball", "sport", "ball", "hoop", "game"] },
  { emoji: "🏐", name: "Volleyball", keywords: ["volleyball", "sport", "ball", "beach"] },
  { emoji: "🏈", name: "Football", keywords: ["football", "american", "sport", "game"] },
  { emoji: "🏉", name: "Rugby", keywords: ["rugby", "sport", "ball", "game"] },
  { emoji: "⚾", name: "Baseball", keywords: ["baseball", "sport", "ball", "game"] },
  { emoji: "🥎", name: "Softball", keywords: ["softball", "sport", "ball", "game"] },
  { emoji: "🏏", name: "Cricket", keywords: ["cricket", "sport", "bat", "game"] },
  { emoji: "🏒", name: "Ice Hockey", keywords: ["hockey", "ice", "sport", "winter"] },
  { emoji: "🏑", name: "Field Hockey", keywords: ["hockey", "field", "sport"] },
  { emoji: "🏓", name: "Ping Pong", keywords: ["ping pong", "table tennis", "sport"] },
  { emoji: "🏸", name: "Badminton", keywords: ["badminton", "sport", "racket", "shuttlecock"] },
  { emoji: "🥊", name: "Boxing Glove", keywords: ["boxing", "sport", "fight", "fitness"] },
  { emoji: "🥋", name: "Martial Arts", keywords: ["karate", "judo", "martial arts", "sport"] },
  { emoji: "🎳", name: "Bowling", keywords: ["bowling", "sport", "game", "activity", "fun"] },
  { emoji: "🛹", name: "Skateboard", keywords: ["skateboard", "skate", "sport", "extreme"] },
  { emoji: "🛼", name: "Roller Skate", keywords: ["roller skate", "skating", "sport"] },
  { emoji: "🚴", name: "Cycling", keywords: ["bike", "cycle", "sport", "ride", "exercise"] },
  { emoji: "🚵", name: "Mountain Biking", keywords: ["mountain bike", "bike", "trail", "outdoor"] },
  { emoji: "🏄", name: "Surfing", keywords: ["surf", "wave", "beach", "water", "sport"] },
  { emoji: "🚣", name: "Rowing", keywords: ["row", "boat", "water", "sport"] },
  { emoji: "⛷️", name: "Skiing", keywords: ["ski", "snow", "winter", "sport", "mountain"] },
  { emoji: "🏂", name: "Snowboarding", keywords: ["snowboard", "snow", "winter", "sport"] },
  { emoji: "⛸️", name: "Ice Skate", keywords: ["ice skating", "skate", "winter", "sport"] },
  { emoji: "🤿", name: "Diving", keywords: ["dive", "snorkel", "water", "ocean", "sport"] },
  { emoji: "🎣", name: "Fishing", keywords: ["fishing", "rod", "fish", "outdoor"] },
  { emoji: "🏕️", name: "Camping", keywords: ["camp", "tent", "outdoor", "nature", "adventure"] },
  { emoji: "🎮", name: "Game Controller", keywords: ["game", "gaming", "play", "video game"] },
  { emoji: "🎲", name: "Game Die", keywords: ["dice", "game", "board game", "play"] },
  { emoji: "🧩", name: "Puzzle Piece", keywords: ["puzzle", "jigsaw", "game", "solve"] },
  { emoji: "🎨", name: "Artist Palette", keywords: ["art", "paint", "craft", "creative"] },
  { emoji: "🛶", name: "Canoe", keywords: ["canoe", "kayak", "paddle", "boat", "water", "rowing"] },
  { emoji: "🥏", name: "Flying Disc", keywords: ["frisbee", "disc", "throw", "lawn game", "outdoor"] },
  { emoji: "🪁", name: "Kite", keywords: ["kite", "fly", "outdoor", "wind", "fun"] },
  { emoji: "🏹", name: "Bow and Arrow", keywords: ["archery", "bow", "arrow", "target", "aim"] },
  { emoji: "🎿", name: "Skis", keywords: ["ski", "skiing", "snow", "winter", "slope"] },
  { emoji: "🛷", name: "Sled", keywords: ["sled", "sledding", "snow", "winter", "toboggan"] },
  { emoji: "🏇", name: "Horse Racing", keywords: ["horse", "riding", "equestrian", "race", "outdoor"] },
  { emoji: "🤺", name: "Fencing", keywords: ["fencing", "sword", "sport", "duel"] },
  { emoji: "♟️", name: "Chess Pawn", keywords: ["chess", "strategy", "game", "board game"] },

  // Time
  { emoji: "⏱️", name: "Stopwatch", keywords: ["stopwatch", "timer", "race", "elapsed"] },
  { emoji: "⏲️", name: "Timer Clock", keywords: ["timer", "countdown", "kitchen", "minutes"] },
  { emoji: "⌛", name: "Hourglass", keywords: ["hourglass", "wait", "time", "deadline"] },
  { emoji: "⏳", name: "Hourglass Flowing", keywords: ["hourglass", "time", "running out", "wait"] },
  { emoji: "📅", name: "Calendar", keywords: ["calendar", "date", "schedule", "agenda"] },
  { emoji: "🗓️", name: "Spiral Calendar", keywords: ["calendar", "planner", "schedule", "agenda"] },

  // Nature & Weather
  { emoji: "🌳", name: "Deciduous Tree", keywords: ["tree", "park", "nature", "outdoor"] },
  { emoji: "🌲", name: "Evergreen Tree", keywords: ["pine", "tree", "forest", "nature"] },
  { emoji: "🌴", name: "Palm Tree", keywords: ["palm", "tropical", "beach", "vacation"] },
  { emoji: "🌵", name: "Cactus", keywords: ["cactus", "desert", "plant", "southwest"] },
  { emoji: "🌷", name: "Tulip", keywords: ["tulip", "flower", "spring", "garden"] },
  { emoji: "🌹", name: "Rose", keywords: ["rose", "flower", "romance", "love"] },
  { emoji: "🌻", name: "Sunflower", keywords: ["sunflower", "flower", "summer", "yellow"] },
  { emoji: "🌼", name: "Daisy", keywords: ["daisy", "flower", "spring", "white"] },
  { emoji: "💐", name: "Bouquet", keywords: ["bouquet", "flowers", "gift", "wedding"] },
  { emoji: "🍀", name: "Four Leaf Clover", keywords: ["luck", "clover", "irish", "shamrock"] },
  { emoji: "🍁", name: "Maple Leaf", keywords: ["maple", "leaf", "fall", "autumn", "canada"] },
  { emoji: "🍂", name: "Fallen Leaves", keywords: ["leaves", "autumn", "fall", "season"] },
  { emoji: "🌙", name: "Crescent Moon", keywords: ["moon", "night", "sleep", "evening"] },
  { emoji: "🌟", name: "Glowing Star", keywords: ["star", "sparkle", "magic", "highlight"] },
  { emoji: "✨", name: "Sparkles", keywords: ["sparkles", "magic", "shiny", "highlight"] },
  { emoji: "🌈", name: "Rainbow", keywords: ["rainbow", "pride", "weather", "colorful"] },
  { emoji: "☁️", name: "Cloud", keywords: ["cloud", "weather", "sky"] },
  { emoji: "⛅", name: "Sun Behind Cloud", keywords: ["partly cloudy", "weather", "sun", "cloud"] },
  { emoji: "🌧️", name: "Cloud with Rain", keywords: ["rain", "weather", "shower", "wet"] },
  { emoji: "⛈️", name: "Thunderstorm", keywords: ["storm", "thunder", "lightning", "weather"] },
  { emoji: "🌨️", name: "Cloud with Snow", keywords: ["snow", "weather", "winter", "cold"] },
  { emoji: "❄️", name: "Snowflake", keywords: ["snowflake", "snow", "winter", "cold"] },
  { emoji: "☃️", name: "Snowman", keywords: ["snowman", "winter", "snow", "holiday"] },
  { emoji: "💧", name: "Droplet", keywords: ["water", "droplet", "drop", "wet"] },
  { emoji: "🐝", name: "Honeybee", keywords: ["bee", "honey", "insect", "nature"] },
  { emoji: "🦋", name: "Butterfly", keywords: ["butterfly", "insect", "nature", "spring"] },
  { emoji: "🐦", name: "Bird", keywords: ["bird", "animal", "nature", "fly"] },
  { emoji: "🐬", name: "Dolphin", keywords: ["dolphin", "ocean", "animal", "marine"] },
  { emoji: "🐢", name: "Turtle", keywords: ["turtle", "slow", "animal", "nature"] },
  { emoji: "🦌", name: "Deer", keywords: ["deer", "wildlife", "animal", "nature", "forest"] },
  { emoji: "🐾", name: "Paw Prints", keywords: ["paw", "pet", "animal", "tracks", "dog"] },
  { emoji: "🌾", name: "Sheaf of Rice", keywords: ["wheat", "field", "harvest", "farm", "grain", "rustic"] },
  { emoji: "🍄", name: "Mushroom", keywords: ["mushroom", "forest", "nature", "foraging"] },
];

// ============================================================
// Render Helper
// ============================================================

/**
 * Converts a stored IconEmojiValue into a ReactNode for rendering.
 * Icons are rendered inside a bg-landing-secondary box.
 * Emojis are rendered bare at a large size.
 * Returns null if the icon name is not found in the registry.
 */
export function renderIconEmoji(value: IconEmojiValue | null | undefined): ReactNode {
  if (!value) return null;

  if (value.type === "emoji") {
    return <span className="text-[32px] leading-none">{value.value}</span>;
  }

  const IconComponent = ICON_REGISTRY[value.value];
  if (!IconComponent) return null;

  return (
    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-indigo-50">
      <IconComponent className="h-6 w-6 text-gray-900" strokeWidth={1.75} />
    </div>
  );
}

/**
 * Renders a small preview of an icon/emoji for use in trigger buttons and list items.
 * Icons render at 20×20 in a 32×32 box. Emojis render at text-lg.
 */
export function renderIconEmojiPreview(value: IconEmojiValue | null | undefined): ReactNode {
  if (!value) return null;

  if (value.type === "emoji") {
    return <span className="text-lg leading-none">{value.value}</span>;
  }

  const IconComponent = ICON_REGISTRY[value.value];
  if (!IconComponent) return null;

  return (
    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-indigo-50">
      <IconComponent className="h-5 w-5 text-gray-900" strokeWidth={1.75} />
    </div>
  );
}
